import { useNavigate } from 'react-router-dom';
import { Button, Input, InputWrapperAuth } from '@/components/ui';
import { z } from 'zod';
import { ArrowRightCircle, Lock, Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { saveStepData } from '@/utils/localStorageUtils';
import { useEffect } from 'react';
import { STORAGE_USER_KEYS } from './UserRegister';
import { useGetUser } from '@/services';
import googleIcon from '@/assets/icons/devicon_google.svg';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/pages/public/landing/components/Header';

const schema = z
	.object({
		email: z.string().max(255, 'Email inválido').email('Email inválido'),
		password: z
			.string()
			.min(6, 'A senha deve ter no mínimo 6 caracteres')
			.max(255, 'A senha deve ter no máximo 255 caracteres')
			.regex(
				/^[0-9A-Za-zÀ-ÖØ-öø-ÿ!@#$%^&*()_+\-= [\]{};':"\\|,.<>/?~`]+$/,
				'A senha contém caracteres inválidos',
			)
			.regex(
				/^(?=.*[A-Za-z])(?=.*\d).+$/,
				'A senha deve conter pelo menos uma letra e um número',
			),
		repeat_password: z.string(),
	})
	.refine((data) => data.password === data.repeat_password, {
		message: 'As senhas não coincidem',
		path: ['repeat_password'],
	});

type FormData = z.infer<typeof schema>;

const FormLabel = ({
	children,
	htmlFor,
}: {
	children: React.ReactNode;
	htmlFor?: string;
}) => (
	<label
		htmlFor={htmlFor}
		className="mb-1 ml-1 block cursor-pointer text-[10px] font-black uppercase tracking-widest text-stone-500 transition-colors hover:text-stone-700"
	>
		{children}
	</label>
);

const UserRegisterGoogle = () => {
	const navigate = useNavigate();

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
	} = useForm<FormData>({
		resolver: zodResolver(schema),
	});

	const { data: retrivedUserData, isLoading } = useGetUser({ isMe: true });

	useEffect(() => {
		if (isLoading) return;
		if (retrivedUserData) {
			setValue('email', retrivedUserData.email);
		} else {
			navigate('/cadastro');
		}
	}, [isLoading, retrivedUserData, navigate, setValue]);

	const onSubmit = (newData: FormData) => {
		if (!retrivedUserData?.id) return;
		saveStepData(STORAGE_USER_KEYS.USER_AUTH, STORAGE_USER_KEYS.STEP_INIT, {
			id: retrivedUserData.id,
			email: retrivedUserData.email,
			password: newData.password,
			repeat_password: newData.repeat_password,
		});
		navigate({
			pathname: '/cadastro/info-complemento',
			search: 'isG=true',
		});
	};

	const iconCommonClass = 'w-4 h-4 text-stone-400';

	return (
		<div className="flex min-h-screen flex-col overflow-y-auto bg-[#f8f9fa] font-sans">
			<Helmet>
				<title>Cadastro com Google | Plumaa</title>
			</Helmet>

			<Header />

			<main className="flex flex-1 items-center justify-center p-2 py-8">
				<div className="flex w-full max-w-[440px] flex-col gap-6 rounded-[2rem] bg-white p-5 shadow-sm md:p-8">
					<div className="space-y-1 text-center">
						<div className="flex items-center justify-center gap-2">
							<img
								src={googleIcon}
								className="size-5"
								alt="Google"
							/>
							<h1 className="text-xl font-black tracking-tight text-stone-900 md:text-2xl">
								{' '}
								Cadastro com Google
							</h1>
						</div>
						<p className="text-[10px] font-medium text-stone-400 md:text-xs">
							{' '}
							Vamos continuar seu cadastro!
						</p>
					</div>

					<form
						onSubmit={handleSubmit(onSubmit)}
						className="space-y-4"
					>
						<div className="space-y-4">
							{' '}
							<div>
								<FormLabel htmlFor="field-email">
									E-MAIL
								</FormLabel>
								<InputWrapperAuth
									className="max-w-none opacity-70"
									icon={<Mail className={iconCommonClass} />}
									error={errors.email?.message}
									value={watch('email')}
									displayError
								>
									<Input
										readOnly
										{...register('email')}
										id="field-email"
										className="h-9 w-full cursor-not-allowed border-stone-200 bg-stone-50 text-sm"
									/>
								</InputWrapperAuth>
							</div>
							<div>
								<div className="mb-1 flex items-center justify-between px-1">
									{' '}
									<FormLabel htmlFor="field-password">
										SENHA
									</FormLabel>
									<span className="text-[9px] font-bold italic text-stone-300">
										{' '}
										mín. 6 caracteres
									</span>
								</div>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Lock className={iconCommonClass} />}
									error={errors.password?.message}
									value={watch('password')}
									displayError
								>
									<Input
										type="password"
										{...register('password')}
										id="field-password"
										placeholder="********"
										className="h-9 w-full border-stone-200 text-sm focus:border-stone-400"
									/>
								</InputWrapperAuth>
							</div>
							<div>
								<FormLabel htmlFor="field-repeat-password">
									CONFIRME A SENHA
								</FormLabel>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Lock className={iconCommonClass} />}
									error={errors.repeat_password?.message}
									value={watch('repeat_password')}
									displayError
								>
									<Input
										type="password"
										{...register('repeat_password')}
										id="field-repeat-password"
										placeholder="********"
										className="h-9 w-full border-stone-200 text-sm focus:border-stone-400"
									/>
								</InputWrapperAuth>
							</div>
						</div>

						<Button
							type="submit"
							className="w-full justify-center rounded-lg border-none bg-[#1a1a1a] py-3 text-sm font-bold text-white transition-all hover:bg-stone-800"
							text="Continuar"
							endIcon={
								<ArrowRightCircle size={14} className="ml-1" />
							}
						/>
					</form>

					<div className="border-t border-stone-50 pt-2 text-center">
						{' '}
						<button
							type="button"
							onClick={() => navigate('/cadastro')}
							className="mx-auto flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-widest text-stone-400 transition-colors hover:text-stone-900"
						>
							Voltar e usar outro método
						</button>
					</div>
				</div>
			</main>
		</div>
	);
};

export { UserRegisterGoogle };
